<template>
  <div class="folder-item__inner-content"
       :class="{ 'folder-item__special': isFolderItemSpecial }"
       :style="{ paddingLeft: leftOffset }"
  >
    <div v-if="!isFolderItemSpecial" style="width: 18px">
      <app-material-icon v-if="loadingChildren"
        color="primary"
        class="spinning"
        name="progress_activity"
      />
      <template v-if="hasChildren">
        <app-material-icon v-if="!loadingChildren"
          :name="areChildrenExpanded ? 'arrow_drop_down' : 'arrow_right'"
          class="expand-icon"
          @click.stop="$emit('load-children')"
        />
      </template>
    </div>

    <AppTooltip :disabled="!iconOnly || (iconOnly && homeLayout)" position="right">
      <template #activator="{ props }">
        <app-material-icon v-bind="props"
          :filled="homeLayout"
          :name="computedIconName"
          class="folder-icon"
          :class="homeLayout ? '' : 'mr-2'"
        />
      </template>
      <span v-if="iconOnly">{{ folderName }}</span>
    </AppTooltip>

    <AppTooltip
      v-if="!iconOnly"
      :open-delay="500"
      :disabled="!hasTooltip"
    >
      <template #activator="{ props }">
        <div v-bind="props"
             ref="folderName"
             class="folder-name text-truncate"
        >
          <span class="text--regular">
            {{ folderName }}
          </span>
        </div>
      </template>
      {{ folderName }}
    </AppTooltip>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import { mapState } from 'vuex'

  import AppTooltip from '@/common/AppTooltip.vue'

  import { RECENT_FOLDER, ROOT_FOLDER } from '../constants/special-folders'

  export type DocumentsSidebarFolder = {
    id: number | string;
    name: string;
    parentId?: number | string | null;
    children?: Array<DocumentsSidebarFolder>;
    hasFolderChildren?: boolean;
    areChildrenExpanded?: boolean;
    numbering?: number;
    icon?: string;
  }

  export default defineComponent({
    name: 'DocumentsSidebarFolderItemContent',
    components: { AppTooltip },
    props: {
      folder: {
        type: Object as PropType<DocumentsSidebarFolder>,
        required: true,
      },
      depth: {
        type: Number,
        default: 0,
      },
      areChildrenExpanded: {
        type: Boolean,
        default: false,
      },
      loadingChildren: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        hasTooltip: false,
      }
    },
    async mounted () {
      await this.$nextTick()
      const folderNameDivElement = (this.$refs['folderName'] as HTMLDivElement)
      this.hasTooltip = folderNameDivElement?.offsetWidth < folderNameDivElement?.scrollWidth
    },
    computed: {
      ...mapState('global', ['mini', 'homeLayout']),
      iconOnly (): boolean {
        return this.homeLayout && this.mini
      },
      computedIconName (): string {
        if (!this.folder.icon) {
          return this.homeLayout && this.hasChildren && this.areChildrenExpanded ? 'folder_open' : 'folder'
        } else {
          return this.folder.icon
        }
      },
      leftOffset (): string {
        const LEVEL_OFFSET = 8
        return `${this.depth * LEVEL_OFFSET}px`
      },
      hasChildren (): boolean {
        return !!this.folder.children && this.folder.children.length > 0
      },
      isFolderItemSpecial (): boolean {
        return this.folder.id === RECENT_FOLDER.id || this.folder.id === ROOT_FOLDER.id
      },
      folderName (): string {
        if (this.folder.numbering) {
          return `${this.folder.numbering} ${this.folder.name}`
        }

        return this.folder.name
      },
    },
  })
</script>

<style lang="scss" scoped>
  .folder-item {
    &__inner-content {
      display: grid;
      align-items: end;
      grid-template-columns: 24px auto 1fr;
      grid-template-areas: "expand-icon folder-icon folder-name";
      cursor: pointer;
    }
    // Special folders can never be expanded.
    &__special {
      grid-template-columns: auto 1fr !important;
      grid-template-areas: "folder-icon folder-name" !important;
    }

    .expand-icon {
      grid-area: expand-icon;
      color: rgb(var(--v-theme-grey-600));
      border-radius: 4px;
      width: 24px;

      &:hover {
        background: rgb(var(--v-theme-grey-100));
      }
    }

    .folder-icon {
      grid-area: folder-icon;
    }

    .folder-name {
      grid-area: folder-name;
    }
  }
</style>
